"use client";

import type { PostHog } from "posthog-js";
import { useEffect, useState } from "react";

/**
 * Guest email-apply template A/B (2026-09-28).
 *
 * On an apply-by-email job, a GUEST who taps "Apply by email — we write it,
 * you send it" hits a login wall (`ApplyEmailHandoff`'s `unauthorized`
 * blocker) — the AI draft needs a stored résumé + userId
 * (`genHuntTailorPack`), which a guest cannot have. PostHog 14d: 18 clicked
 * the CTA → 13 prepared → 8 opened → 4 sent, with session replay showing the
 * login wall as the recurring stop.
 *
 * `template` swaps that wall, for guests only, with a ready-to-send (no AI,
 * no server call) template email for the specific job — built client-side
 * from data the page already rendered (job title/company/apply address), via
 * the same Gmail/mailto URL builders the signed-in draft uses
 * (`@/lib/apply/gmail`, `@/lib/apply/handoff`). `control` keeps today's wall.
 *
 * PostHog feature flag "guest-email-apply-draft" (multivariate:
 * control/template) is the SOLE switch — see root `CLAUDE.md` → Forbidden
 * Actions (never shadow a flag with a hardcoded constant). Manifest entry:
 * `src/lib/analytics/flag-manifest.ts`.
 */
export const GUEST_EMAIL_APPLY_DRAFT_FLAG = "guest-email-apply-draft";

export type GuestEmailApplyDraftVariant = "control" | "template";

function isGuestEmailApplyDraftVariant(
	v: unknown,
): v is GuestEmailApplyDraftVariant {
	return v === "control" || v === "template";
}

/**
 * Resolves the guest email-apply draft variant.
 *
 * The flag is read ONLY while `enabled` (the guest is looking at the
 * email-apply login wall). `getFeatureFlag` records `$feature_flag_called`,
 * so the experiment's exposure set is exactly the guests who saw control or
 * template, not every visitor who rendered the handoff.
 *
 * Starts on `"control"` (today's login wall) and flips only when PostHog
 * answers with a known variant, including a late `/decide` via
 * `onFeatureFlags`. Missing/undefined/unknown variant ⇒ `control`.
 */
export function useGuestEmailApplyDraftVariant(
	posthog:
		| Pick<PostHog, "getFeatureFlag" | "onFeatureFlags">
		| null
		| undefined,
	enabled: boolean,
): GuestEmailApplyDraftVariant {
	const [variant, setVariant] =
		useState<GuestEmailApplyDraftVariant>("control");

	useEffect(() => {
		if (!enabled || !posthog) return;
		const read = () => {
			const raw = posthog.getFeatureFlag(GUEST_EMAIL_APPLY_DRAFT_FLAG);
			if (isGuestEmailApplyDraftVariant(raw)) setVariant(raw);
		};
		read();
		return posthog.onFeatureFlags(read);
	}, [enabled, posthog]);

	return variant;
}
